import { useEffect } from "react";
import {
  DndContext,
  DragEndEvent,
  closestCorners,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import toast from "react-hot-toast";
import { Search } from "lucide-react";
import { useKanbanStore } from "../../store/kanbanStore";
import { decodeTokenService } from "../../services/decodeTokenService";
import KanbanColumn from "../ChatPage/components/KanbanColumn";
import Loader from "../../components/common/Loader";
import { useAuth } from "../../context/AuthContext";
import { dictionary } from "../../utils/dictionary";

const COLUMNS = [
  { id: "awaiting", title: "En espera", color: "bg-amber-400" },
  { id: "confirmed", title: "Confirmado", color: "bg-emerald-400" },
  { id: "rejected", title: "Rechazado", color: "bg-red-400" },
  { id: "expired", title: "Expirado", color: "bg-gray-400" },
];

const STATUS_MAP: Record<string, string> = {
  awaiting: "awaiting_response",
  confirmed: "confirmed",
  rejected: "rejected",
  expired: "expired",
};

const KanbanContent = () => {
  const { language } = useAuth();
  const { columns, loading, searchQuery, fetchKanban, moveCard, setSearchQuery } = useKanbanStore();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  useEffect(() => {
    const ownerId = decodeTokenService.getUserIdFromToken();
    if (ownerId) fetchKanban(ownerId);
  }, []);

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;

    const cardId = active.id as number;
    const targetColId = over.id as string;

    let sourceColId = "";
    let card: any;
    for (const [colId, cards] of Object.entries(columns)) {
      const found = cards.find((c) => c.id === cardId);
      if (found) {
        sourceColId = colId;
        card = found;
        break;
      }
    }

    if (!card || sourceColId === targetColId) return;

    if (card.ai_enabled) {
      toast.dismiss();
      toast.error("Desactiva la IA desde el chat para mover este pedido.");
      return;
    }

    const targetStatus = STATUS_MAP[targetColId];
    if (!targetStatus) return;

    const success = await moveCard(cardId, sourceColId, targetColId, targetStatus);
    toast.dismiss();
    if (success) {
      toast.success(`Pedido #${card.order_id} movido`);
    } else {
      toast.error("Error al cambiar el estado");
    }
  };

  const filteredColumns: Record<string, any[]> = {};
  for (const [colId, cards] of Object.entries(columns)) {
    if (!searchQuery) {
      filteredColumns[colId] = cards;
    } else {
      const q = searchQuery.toLowerCase();
      filteredColumns[colId] = cards.filter(
        (c) =>
          c.customer_name?.toLowerCase().includes(q) ||
          c.customer_phone?.includes(q) ||
          String(c.order_id).includes(q)
      );
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full">
        <Loader />
      </div>
    );
  }

  return (
    <div className="h-full p-8 font-['Plus_Jakarta_Sans'] overflow-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {dictionary["kanban-title-global"]?.[language] || "Kanban de Confirmaciones"}
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {dictionary["kanban-subtitle-global"]?.[language] || "Arrastra los pedidos para cambiar su estado"}
          </p>
        </div>
        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar por cliente, teléfono u orden..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCorners} onDragEnd={handleDragEnd}>
        <div className="flex gap-4 overflow-x-auto pb-4">
          {COLUMNS.map((col) => (
            <KanbanColumn
              key={col.id}
              id={col.id}
              title={col.title}
              cards={filteredColumns[col.id] || []}
              color={col.color}
            />
          ))}
        </div>
      </DndContext>
    </div>
  );
};

export default KanbanContent;
